import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

// EL RECORRIDO GUIADO PUEDE HABLAR DENTRO DE UN DIÁLOGO (25-09, pasos de «Lo hago yo»). Su
// cuadro va fuera del diálogo, y Radix toma cualquier clic de fuera por «cerrar»: pulsar
// «Siguiente» cerraba la comida que se estaba explicando. Los clics del recorrido no cuentan.
const esDelRecorrido = (e) => !!e?.target?.closest?.('.driver-popover, .driver-overlay');

const DialogContent = React.forwardRef(({ className, children, onInteractOutside, ...props }, ref) => {
  // Radix deja document.body con pointer-events:none si el contenido se desmonta en el
  // mismo commit que open=false (patrón habitual aquí: {abierto && <DialogContent>}).
  // El body muerto se traga el siguiente clic de toda la app: era el "hay que pulsar
  // dos o tres veces" del recorrido de Juan (doc 57, F1).
  React.useEffect(() => () => {
    setTimeout(() => {
      if (!document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]')) {
        document.body.style.pointerEvents = "";
      }
    }, 0);
  }, []);
  return (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        // Sin animación de salida a propósito (doc 57, F1): Radix solo desmonta el diálogo
        // cuando la animación de cierre termina, y si ese evento se pierde el nodo se queda
        // en el DOM con el body en pointer-events:none y la app entera deja de responder al
        // primer clic. La entrada sí anima; el cierre es instantáneo y siempre desmonta.
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg max-h-[92dvh] overflow-y-auto",
        className
      )}
      onInteractOutside={(e) => { if (esDelRecorrido(e)) { e.preventDefault(); return; } if (onInteractOutside) onInteractOutside(e); }}
      {...props}>
      {children}
      {/* EL BOTÓN DE CERRAR, CON SITIO PARA EL DEDO Y EN CASTELLANO (1-09-2026).

          Eran 17 x 17 px: menos de la mitad de lo que se puede pulsar cómodamente, y en el
          calendario caía justo encima de la esquina del botón de mes siguiente, así que al
          ir a avanzar de mes se cerraba el diálogo. Con `p-2 -m-2` el área que recibe el
          clic crece hasta 33 px sin mover ni un píxel la equis que se ve, así que ningún
          diálogo cambia de aspecto.

          Y decía «Close» a los lectores de pantalla. Es el texto que trae shadcn de serie y
          se había quedado sin traducir en el único sitio donde no se ve. */}
      <DialogPrimitive.Close
        className="absolute right-4 top-4 p-2 -m-2 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <X className="h-4 w-4" />
        <span className="sr-only">Cerrar</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
  );
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
